import type { ChipColor } from '@/components/base/BaseChip.vue'

const HUE_BY_CHIP_COLOR: [ChipColor, number][] = [
  ['red', 0],
  ['orange', 19],
  ['yellow', 38],
  ['green', 142],
  ['blue', 214],
  ['indigo', 234],
  ['violet', 265]
]

const CHIP_COLORS: ChipColor[] = [
  'gray',
  'red',
  'blue',
  'green',
  'yellow',
  'violet',
  'orange',
  'indigo',
  'emerald',
  'slate'
]

const MIN_SATURATION = 0.15

const toHueAndSaturation = (hex: string) => {
  const value = parseInt(hex, 16)
  const r = ((value >> 16) & 255) / 255
  const g = ((value >> 8) & 255) / 255
  const b = (value & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  if (delta === 0) return { hue: 0, saturation: 0 }

  const lightness = (max + min) / 2
  const saturation = delta / (1 - Math.abs(2 * lightness - 1))
  let hue = 0
  if (max === r) hue = ((g - b) / delta) % 6
  else if (max === g) hue = (b - r) / delta + 2
  else hue = (r - g) / delta + 4

  return { hue: (hue * 60 + 360) % 360, saturation }
}

const hueDistance = (a: number, b: number) => {
  const distance = Math.abs(a - b) % 360
  return Math.min(distance, 360 - distance)
}

export const hexToChipColor = (color: string | null | undefined): ChipColor => {
  if (!color) return 'gray'
  if (CHIP_COLORS.includes(color as ChipColor)) return color as ChipColor

  const hex = color.replace('#', '')
  if (!/^[0-9a-f]{6}$/i.test(hex)) return 'gray'

  const { hue, saturation } = toHueAndSaturation(hex)
  if (saturation < MIN_SATURATION) return 'gray'

  return HUE_BY_CHIP_COLOR.reduce((closest, current) =>
    hueDistance(hue, current[1]) < hueDistance(hue, closest[1]) ? current : closest
  )[0]
}
